/**
 * Consent Mode v2 state (D22).
 *
 * Greece is EEA: from the moment Google Ads runs (item 22), Google requires
 * consent signals from the banner — without them GA4 and Ads conversion data
 * degrade and remarketing audiences never build. So this is conversion
 * infrastructure, not a compliance sticker.
 *
 * THE ORDER IS THE WHOLE MECHANISM:
 *   1. _document injects `gtag('consent','default', …all denied)` inline,
 *      before any tag can load.
 *   2. gtag.js loads (via @next/third-parties). With storage denied it sends
 *      cookieless pings only — the Consent-Mode-approved behaviour.
 *   3. The visitor answers the banner → `applyConsent` fires
 *      `gtag('consent','update', …)` and the choice persists locally.
 *   4. Returning visitors get their stored choice re-applied before paint via
 *      the same inline bootstrap.
 *
 * Signal groups mirror the CMS cookie-category component (a CLOSED enum there,
 * because a typo in a hand-written signal name fails silently — the tag just
 * never receives consent and the failure looks like mild under-reporting).
 */

export type SignalGroup = "Necessary" | "Analytics" | "Advertising" | "Functionality" | "Personalization";

export type ConsentChoices = Partial<Record<SignalGroup, boolean>>;

export const CONSENT_STORAGE_KEY = "pw-consent-v1";

/** gtag consent keys per group. Necessary maps to the always-granted pair. */
const SIGNALS: Record<SignalGroup, string[]> = {
  Necessary: ["security_storage", "functionality_storage"],
  Functionality: ["functionality_storage"],
  Analytics: ["analytics_storage"],
  Advertising: ["ad_storage", "ad_user_data", "ad_personalization"],
  Personalization: ["personalization_storage"],
};

export const readConsent = (): ConsentChoices | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_STORAGE_KEY);
    return raw ? (JSON.parse(raw) as ConsentChoices) : null;
  } catch {
    return null;
  }
};

export const applyConsent = (choices: ConsentChoices) => {
  if (typeof window === "undefined") return;
  const update: Record<string, "granted" | "denied"> = {};
  for (const [group, keys] of Object.entries(SIGNALS) as [SignalGroup, string[]][]) {
    const granted = group === "Necessary" ? true : !!choices[group];
    for (const key of keys) {
      // a key granted by any group stays granted
      if (update[key] !== "granted") update[key] = granted ? "granted" : "denied";
    }
  }
  window.gtag?.("consent", "update", update);
  // mirror for the SERVER (proxy.ts gates the storefront.Identity session
  // on this — localStorage is invisible to middleware). 180 days, Lax.
  document.cookie = `pw-consent-analytics=${choices.Analytics ? "1" : "0"}; path=/; max-age=15552000; SameSite=Lax`;
  // measure the choice itself. By definition this only reaches GA when
  // Analytics was granted — it reports the accept mix among consenters,
  // not an overall consent rate (denials are invisible, correctly so).
  window.gtag?.("event", "consent_update", {
    event_category: "consent",
    event_label: (Object.keys(SIGNALS) as SignalGroup[])
      .filter((g) => g === "Necessary" || !!choices[g])
      .join(","),
  });
  try {
    window.localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(choices));
  } catch {
    /* storage full/blocked: the session still honours the choice via gtag */
  }
  window.dispatchEvent(new CustomEvent("pw-consent-changed", { detail: choices }));
};

/**
 * Inline bootstrap for _document — MUST run before gtag.js.
 * Declares dataLayer, sets the denied-by-default baseline, then re-applies a
 * stored choice so returning visitors do not flash back to denied.
 * Kept dependency-free and ES5-safe: it executes before the bundle.
 *
 * The two `set` lines are the Ads-launch (15/09) half of Consent Mode:
 * `url_passthrough` carries gclid through internal navigation in URL params
 * while ad_storage is denied (conversions still attribute), and
 * `ads_data_redaction` strips ad identifiers from the cookieless pings.
 * Both are no-ops once the visitor grants Advertising.
 */
export const CONSENT_BOOTSTRAP = `
window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
window.gtag = window.gtag || gtag;
gtag('set','url_passthrough',true);
gtag('set','ads_data_redaction',true);
gtag('consent','default',{
  ad_storage:'denied', ad_user_data:'denied', ad_personalization:'denied',
  analytics_storage:'denied', personalization_storage:'denied',
  functionality_storage:'granted', security_storage:'granted',
  wait_for_update: 500
});
try {
  var stored = JSON.parse(localStorage.getItem('${CONSENT_STORAGE_KEY}') || 'null');
  if (stored) {
    var u = {};
    if (stored.Analytics) u.analytics_storage = 'granted';
    if (stored.Advertising) { u.ad_storage='granted'; u.ad_user_data='granted'; u.ad_personalization='granted'; }
    if (stored.Personalization) u.personalization_storage = 'granted';
    if (Object.keys(u).length) gtag('consent','update', u);
    document.cookie = 'pw-consent-analytics=' + (stored.Analytics ? '1' : '0') + '; path=/; max-age=15552000; SameSite=Lax';
  }
} catch (e) {}
`.trim();
